/**
 * Businesses Client
 * Business operations and metrics retrieval
 */

import type { Business, DashboardMetrics } from './types';
import { API_URL } from './constants';
import { createHeaders, getCurrentOperator, createFullHeaders, handleApiError, logApiCall } from './base-client';

// =============================================================================
// Dashboard Metrics
// =============================================================================

/**
 * Get dashboard metrics for the operator
 * Returns aggregated metrics or business-specific metrics if businessId is provided.
 *
 * @param businessId - Optional business ID for filtered metrics
 * @returns Dashboard metrics
 *
 * @example
 * ```ts
 * // Get all metrics
 * const metrics = await getDashboardMetrics();
 *
 * // Get metrics for specific business
 * const businessMetrics = await getDashboardMetrics('biz-001');
 * ```
 */
export async function getDashboardMetrics(businessId?: string | null): Promise<DashboardMetrics> {
  const startTime = Date.now();
  const emptyMetrics: DashboardMetrics = {
    totalConversations: 0,
    activeConversations: 0,
    completedBookings: 0,
    pendingBookings: 0,
    conversionRate: 0,
    avgResponseTime: 0,
  };

  try {
    const params = new URLSearchParams();
    if (businessId) params.append('businessId', businessId);

    const endpoint = '/api/admin/metrics';
    const url = params.toString()
      ? `${API_URL}${endpoint}?${params.toString()}`
      : `${API_URL}${endpoint}`;

    const response = await fetch(url, {
      headers: createHeaders(),
    });

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const json = await response.json();
    const raw = json.data || json;

    // Map API response fields to DashboardMetrics shape
    return {
      totalConversations: raw.totalConversations ?? raw.conversationsToday ?? 0,
      activeConversations: raw.activeConversations ?? 0,
      completedBookings: raw.completedBookings ?? raw.bookingsToday ?? 0,
      pendingBookings: raw.pendingBookings ?? 0,
      conversionRate: raw.conversionRate ?? 0,
      avgResponseTime: raw.avgResponseTime ?? 0,
    };
  } catch (error) {
    handleApiError(error, 'getDashboardMetrics', { businessId });
    return emptyMetrics;
  }
}

// =============================================================================
// Business Operations
// =============================================================================

/**
 * Get businesses accessible to the current operator
 * ADMIN sees all businesses; other roles see only assigned businesses.
 *
 * @returns List of accessible businesses
 *
 * @example
 * ```ts
 * const businesses = await getBusinesses();
 * console.log(`Access to ${businesses.length} businesses`);
 * ```
 */
export async function getBusinesses(): Promise<Business[]> {
  const startTime = Date.now();
  const operator = getCurrentOperator();

  if (!operator) {
    return [];
  }

  try {
    const response = await fetch('/api/operator/businesses', {
      headers: createFullHeaders(),
    });

    logApiCall('/api/operator/businesses', 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403) {
        return [];
      }
      throw new Error(`API error: ${response.status}`);
    }

    const json = await response.json();
    // API returns { success, data: Business[] } - extract the array
    return json.data || json;
  } catch (error) {
    handleApiError(error, 'getBusinesses');
    return [];
  }
}

/**
 * Get a single business by ID
 *
 * @param id - Business ID
 * @returns Business or null if not found
 *
 * @example
 * ```ts
 * const business = await getBusinessById('biz-001');
 * if (business) {
 *   console.log(`Business: ${business.name}`);
 * }
 * ```
 */
export async function getBusinessById(id: string): Promise<Business | null> {
  const startTime = Date.now();
  const endpoint = `/api/admin/businesses/${id}`;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: createHeaders(),
    });

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    handleApiError(error, 'getBusinessById', { businessId: id });
    return null;
  }
}

/**
 * Get metrics for a specific business
 *
 * @param businessId - Business ID
 * @returns Business metrics
 */
export async function getBusinessMetrics(businessId: string): Promise<DashboardMetrics> {
  return getDashboardMetrics(businessId);
}

/**
 * Check if operator has access to a specific business
 *
 * @param businessId - Business ID to check
 * @returns true if operator has access
 */
export function hasBusinessAccess(businessId: string): boolean {
  const operator = getCurrentOperator();
  if (!operator) return false;

  // ADMIN has access to all businesses
  if (operator.role === 'ADMIN') return true;

  return operator.businessIds.includes(businessId);
}
